// Centralized FR/EN copy. Keys are stable; values are the displayed strings.
export const translations = {
    en: {
        nav: {
            about: "About",
            thesis: "Thesis",
            collaboration: "Collaboration",
            contact: "Contact",
            cta: "Submit your project",
            langLabel: "Language",
        },
        hero: {
            label: "Premium Advisory — B2B Deep Tech",
            h1Line1: "Few mandates.",
            h1Line2: "Full commitment.",
            h1Line3: "We back execution.",
            subtitle:
                "A selective advisory platform for B2B founders who need capital access, market structure, and strategic co-sales.",
            ctaPrimary: "Submit your venture",
            ctaSecondary: "Read our thesis",
            comingNote: "US, Qatar, Singapore — coming",
            tags: ["B2B", "IoT", "AI", "Health", "Climate", "Infrastructure"],
        },
        whatWeDo: {
            label: "What we do",
            headlineLead:
                "We partner with founders to remove the blockers that matter:",
            headlineMuted:
                "funding, governance, market access, and strategic co-sales.",
            items: [
                {
                    title: "Selective screening",
                    body: "Paying customers, a repeatable sales process, and the discipline to execute. We don't work with pre-revenue projects.",
                },
                {
                    title: "Structure & strategy",
                    body: "Board-ready governance, GTM clarity, investor narrative. We work alongside the founding team — not from a distance.",
                },
                {
                    title: "Capital & access",
                    body: "Direct introductions to relevant capital. Co-investment, advisory roles, and enterprise co-sales across Europe and beyond.",
                },
            ],
        },
        thesis: {
            label: "Our thesis",
            headlineBefore: "B2B deep tech with",
            headlineAccent: "real customers",
            headlineAfter: "and solvable economics.",
            verticalsLabel: "Verticals",
            verticals: [
                "IoT",
                "Health",
                "AI",
                "Climate",
                "Industrial & Infrastructure",
                "Deep Tech",
            ],
            lookingForLabel: "What we look for",
            lookingFor: [
                "Paying customers.",
                "A repeatable motion.",
                "A founding team that executes.",
            ],
            moatLabel: "Team & moat",
            moat: [
                {
                    title: "Coachability",
                    body: "Founders who integrate feedback without losing conviction. The rarest combination in deep tech.",
                },
                {
                    title: "Strong tech lead",
                    body: "A technical anchor who owns the architecture and can defend it in front of investors, partners, and enterprise buyers.",
                },
                {
                    title: "IP / data / firmware",
                    body: "Real IP, genuine expertise, tangible vision. Something that exists before the pitch deck.",
                },
            ],
        },
        collaboration: {
            label: "Collaboration modes",
            headlineBefore: "We adapt involvement to fit the venture stage.",
            headlineAccent: "Equity, advisory, or co-sales",
            headlineAfter:
                "— always with clear conditions and full commitment.",
            modes: [
                {
                    title: "Equity Partner",
                    body: "Minority, hands-on co-design of product, GTM, pricing and governance.",
                },
                {
                    title: "Advisory / Board seat",
                    body: "NED-level cadence: KPI, finance discipline, hiring and strategic focus.",
                },
                {
                    title: "Strategic co-sales",
                    body: "Access to enterprise buyers across the EU & beyond.",
                },
            ],
            statsACount: "70",
            statsALabel: "Countries reached",
            statsBCount: "60",
            statsBLabel: "VCs, Family Offices & BA associations",
        },
        contact: {
            label: "Submit your venture",
            headlineAccent: "Every submission",
            headlineRest: "is read personally.",
            subtitle:
                "If the fit is there, we'll schedule a qualification call within 48 hours.",
            noDeck: [
                "No deck required.",
                "No free consulting.",
                "Qualified submissions only.",
            ],
            sections: {
                s1: "Identification",
                s2: "Your venture",
                s3: "What you need",
                s4: "Current challenges",
                s5: "Qualification",
            },
            fields: {
                firstName: "First name",
                lastName: "Last name",
                email: "Email",
                phone: "Phone",
                company: "Company",
                vertical: "Vertical",
                stage: "Stage",
                description: "One-sentence description",
                descriptionPlaceholder: "We help [who] do [what] by [how].",
                verticalOther: "Specify vertical",
                verticalOtherHint:
                    "Optional — only if 'Other' selected above.",
                needs: "What you need",
                needsHint: "Select all that apply (at least one).",
                challenges:
                    "What is the concrete problem you cannot solve alone?",
                challengesPlaceholder:
                    "Be specific. What is blocking you right now?",
                revenue: "Revenue",
                targetGeo: "Primary target geography",
                targetGeoPlaceholder: "e.g. DACH, France, Nordics",
                selectPlaceholder: "Select…",
                otherOption: "Other — please specify below",
            },
            consentBefore:
                "I consent to my information being processed by 0g Holding SA in the context of an advisory qualification, in accordance with the",
            consentLink: "privacy policy",
            consentAfter: ".",
            submit: "Submit your venture",
            submitting: "Submitting…",
            submitNote:
                "No deck required. No free consulting. Qualified submissions only.",
            success:
                "Submission received. We read every venture personally — if there's a fit, you'll hear from us within 48 hours.",
            errorGeneric:
                "Something went wrong. Please retry or write to info@alpe.vc.",
            errorMissing: "Missing field:",
            errorAtLeastOne:
                "Select at least one item under 'What you need'.",
            errorEmail: "Please enter a valid email address.",
            errorConsent:
                "You must accept the privacy policy to submit your venture.",
            errorRateLimit:
                "Too many submissions from your network. Please try again in an hour, or write to info@alpe.vc.",
        },
        footer: {
            initiative: "Alpe.vc — an initiative of 0g Holding SA.",
            address: "Rue du Cavour 1, 1203 Genève, Switzerland",
            colNavigation: "Navigation",
            colEcosystem: "Network & Ecosystem",
            ecosystemA: "Active in 70+ countries.",
            ecosystemB:
                "Connected to 60+ VCs, Family Offices & Business Angel Associations.",
            comingNote: "(US, Qatar, Singapore — coming)",
            rights: "© 2025 Alpe.vc — All rights reserved",
            legalLink: "Legal notice",
            privacyLink: "Privacy",
            hostedOn: "Hosted on Vercel",
            dataController: "Data Controller: 0g Holding SA (CH)",
        },
        legalShared: {
            backHome: "Back to home",
            lastUpdated: "Last updated · May 2026",
        },
        legal: {
            title: "Legal notice",
            s1Label: "Publisher",
            s1Heading: "Website editor",
            s1Body: [
                "Alpe.vc is an initiative of 0g Holding SA, a Swiss limited company registered in the canton of Geneva.",
                [
                    "Company name: 0g Holding SA",
                    "Registered office: Rue du Cavour 1, 1203 Genève, Switzerland",
                    "Contact: info@alpe.vc",
                    "Director of publication: Eric Brisset",
                ],
            ],
            s2Label: "Hosting",
            s2Heading: "Site hosting",
            s2Body: [
                "The alpe.vc website is hosted by Vercel Inc., 440 N Barranca Avenue #4133, Covina, CA 91723, USA — vercel.com.",
                "The backend API and database (Supabase) are hosted in the European Union (Ireland / Frankfurt regions).",
            ],
            s3Label: "Intellectual property",
            s3Heading: "Content ownership",
            s3Body: [
                "All content published on alpe.vc — including but not limited to text, logo, graphical identity, photographs, illustrations, structure and source code — is the exclusive property of 0g Holding SA, unless otherwise stated.",
                "Any reproduction, representation, modification, publication or adaptation of any element of the site, by any process whatsoever, is forbidden without the prior written authorisation of 0g Holding SA.",
            ],
            s4Label: "Liability",
            s4Heading: "Limitation of liability",
            s4Body: [
                "The information provided on alpe.vc is given on an informational basis only and does not constitute investment, legal, accounting, tax or any other professional advice. 0g Holding SA makes no warranty as to the completeness, accuracy or timeliness of the information presented.",
                "0g Holding SA cannot be held liable for any direct or indirect damage resulting from access to or use of the alpe.vc website.",
            ],
            s5Label: "Governing law",
            s5Heading: "Applicable law & jurisdiction",
            s5Body: [
                "The alpe.vc website and any relationship arising from its use are governed by Swiss law.",
                "Any dispute that cannot be resolved amicably shall fall under the exclusive jurisdiction of the competent courts of the canton of Geneva, Switzerland.",
            ],
        },
        privacy: {
            title: "Privacy policy",
            s1Label: "Overview",
            s1Heading: "Who we are",
            s1Body: [
                "This privacy policy describes how 0g Holding SA, acting as data controller under EU Regulation 2016/679 (GDPR) and the Swiss Federal Act on Data Protection (FADP), collects, uses and protects personal data submitted via the alpe.vc website.",
                "For any question or request relating to your personal data, contact us at info@alpe.vc.",
            ],
            s2Label: "Data collected",
            s2Heading: "What we collect",
            s2Body: [
                "We only collect data you actively provide through the submission form. Specifically:",
                [
                    "Identification: first name, last name, business email, phone, company name",
                    "Venture information: vertical, stage, one-sentence description, revenue range, target geography",
                    "Needs and challenges: areas where you seek support, free-text description of your current blockers",
                ],
                "No analytics cookies, no tracking pixels, no third-party advertising trackers are deployed on this site.",
            ],
            s3Label: "Purpose & legal basis",
            s3Heading: "Why we process your data",
            s3Body: [
                "Your data is processed for the sole purpose of evaluating a potential advisory relationship between you and Alpe.vc, including qualifying your submission and contacting you back if relevant.",
                "Legal bases (GDPR Art. 6):",
                [
                    "Your explicit consent given via the form (Art. 6.1.a)",
                    "Our legitimate interest in evaluating advisory opportunities and managing pre-contractual exchanges (Art. 6.1.f)",
                ],
            ],
            s4Label: "Recipients",
            s4Heading: "Who has access to your data",
            s4Body: [
                "Your data is accessed exclusively by Eric Brisset and authorised members of 0g Holding SA. It is never sold, rented or shared with third parties for marketing purposes.",
                "We rely on the following sub-processors to operate the service:",
                [
                    "Supabase (database hosting — EU regions, Ireland / Frankfurt)",
                    "Google Workspace / Gmail (email delivery — under the EU-US Data Privacy Framework)",
                    "Vercel (website hosting — under the EU-US Data Privacy Framework)",
                ],
            ],
            s5Label: "Retention",
            s5Heading: "How long we keep your data",
            s5Body: [
                "Submissions that do not lead to an engagement are retained for up to 24 months and then deleted from our active database.",
                "If an advisory engagement starts, your data is retained for the duration of the engagement and for the legal retention period applicable to commercial records under Swiss law (typically 10 years).",
            ],
            s6Label: "Your rights",
            s6Heading: "Your rights under GDPR / FADP",
            s6Body: [
                "You have the right to access, rectify, delete, restrict or object to the processing of your personal data, as well as the right to data portability.",
                "To exercise any of these rights, write to info@alpe.vc. We respond within 30 days.",
                "If you consider that your rights have not been respected, you may also lodge a complaint with the competent supervisory authority — the Swiss Federal Data Protection and Information Commissioner (FDPIC) in Switzerland, or your local EU data protection authority.",
            ],
            s7Label: "Security",
            s7Heading: "How we protect your data",
            s7Body: [
                "Data is transmitted over HTTPS and stored in an encrypted database. Access is restricted to authorised personnel under strict authentication.",
                "Despite all reasonable precautions, no transmission or storage method is 100% secure. We commit to notifying you and the competent authority without undue delay in the event of a breach affecting your personal data.",
            ],
        },
        verticalOptions: {
            IoT: "IoT",
            Health: "Health",
            AI: "AI",
            Climate: "Climate",
            "Industrial & Infrastructure": "Industrial & Infrastructure",
            "Deep Tech": "Deep Tech",
            Other: "Other",
        },
        stageOptions: {
            "First customers": "First customers",
            "Established traction": "Established traction",
            Scale: "Scale",
        },
        revenueOptions: {
            "Pre-revenue": "Pre-revenue",
            "<50k€": "<50k€",
            "50k–200k€": "50k–200k€",
            "200k–1M€": "200k–1M€",
            "1M€+": "1M€+",
        },
        needsOptions: {
            Capital: "Capital",
            "GTM / Market access": "GTM / Market access",
            "Europe entry": "Europe entry",
            "Board / Governance": "Board / Governance",
            Combination: "Combination",
        },
    },

    fr: {
        nav: {
            about: "À propos",
            thesis: "Thèse",
            collaboration: "Collaboration",
            contact: "Contact",
            cta: "Soumettre votre projet",
            langLabel: "Langue",
        },
        hero: {
            label: "Conseil Premium — B2B Deep Tech",
            h1Line1: "Peu de mandats.",
            h1Line2: "Engagement total.",
            h1Line3: "Nous misons sur l'exécution.",
            subtitle:
                "Une plateforme de conseil sélective pour les fondateurs B2B qui ont besoin d'accès au capital, de structure de marché et de co-vente stratégique.",
            ctaPrimary: "Soumettre votre projet",
            ctaSecondary: "Lire notre thèse",
            comingNote: "US, Qatar, Singapour — bientôt",
            tags: ["B2B", "IoT", "IA", "Santé", "Climat", "Infrastructure"],
        },
        whatWeDo: {
            label: "Ce que nous faisons",
            headlineLead:
                "Nous accompagnons les fondateurs pour lever les blocages qui comptent :",
            headlineMuted:
                "financement, gouvernance, accès au marché et co-vente stratégique.",
            items: [
                {
                    title: "Sélection rigoureuse",
                    body: "Clients payants, processus de vente reproductible et discipline d'exécution. Nous ne travaillons pas avec des projets pré-revenu.",
                },
                {
                    title: "Structure & stratégie",
                    body: "Gouvernance prête pour un board, clarté GTM, narratif investisseurs. Nous travaillons aux côtés de l'équipe fondatrice — pas à distance.",
                },
                {
                    title: "Capital & accès",
                    body: "Introductions directes aux capitaux pertinents. Co-investissement, rôles consultatifs, co-vente entreprise en Europe et au-delà.",
                },
            ],
        },
        thesis: {
            label: "Notre thèse",
            headlineBefore: "Du B2B deep tech avec de",
            headlineAccent: "vrais clients",
            headlineAfter: "et une économie viable.",
            verticalsLabel: "Verticales",
            verticals: [
                "IoT",
                "Santé",
                "IA",
                "Climat",
                "Industrie & Infrastructure",
                "Deep Tech",
            ],
            lookingForLabel: "Ce que nous cherchons",
            lookingFor: [
                "Des clients payants.",
                "Un mouvement reproductible.",
                "Une équipe fondatrice qui exécute.",
            ],
            moatLabel: "Équipe & moat",
            moat: [
                {
                    title: "Capacité à apprendre",
                    body: "Des fondateurs qui intègrent les retours sans perdre leur conviction. La combinaison la plus rare en deep tech.",
                },
                {
                    title: "Lead technique solide",
                    body: "Une ancre technique qui possède l'architecture et sait la défendre devant investisseurs, partenaires et acheteurs entreprise.",
                },
                {
                    title: "IP / data / firmware",
                    body: "Une vraie IP, une expertise réelle, une vision tangible. Quelque chose qui existe avant le pitch deck.",
                },
            ],
        },
        collaboration: {
            label: "Modes de collaboration",
            headlineBefore:
                "Nous adaptons notre implication au stade du projet.",
            headlineAccent: "Equity, conseil ou co-vente",
            headlineAfter:
                "— toujours avec des conditions claires et un engagement total.",
            modes: [
                {
                    title: "Partenaire equity",
                    body: "Participation minoritaire, co-design hands-on du produit, GTM, pricing et gouvernance.",
                },
                {
                    title: "Conseil / siège au board",
                    body: "Cadence niveau NED : KPI, discipline financière, recrutement et focus stratégique.",
                },
                {
                    title: "Co-vente stratégique",
                    body: "Accès à des acheteurs entreprise dans l'UE et au-delà.",
                },
            ],
            statsACount: "70",
            statsALabel: "Pays atteints",
            statsBCount: "60",
            statsBLabel: "VCs, Family Offices & associations de BA",
        },
        contact: {
            label: "Soumettre votre projet",
            headlineAccent: "Chaque soumission",
            headlineRest: "est lue personnellement.",
            subtitle:
                "Si l'adéquation est là, nous planifions un appel de qualification sous 48 heures.",
            noDeck: [
                "Aucun deck requis.",
                "Pas de conseil gratuit.",
                "Soumissions qualifiées uniquement.",
            ],
            sections: {
                s1: "Identification",
                s2: "Votre projet",
                s3: "Ce dont vous avez besoin",
                s4: "Défis actuels",
                s5: "Qualification",
            },
            fields: {
                firstName: "Prénom",
                lastName: "Nom",
                email: "Email",
                phone: "Téléphone",
                company: "Société",
                vertical: "Verticale",
                stage: "Stade",
                description: "Description en une phrase",
                descriptionPlaceholder:
                    "Nous aidons [qui] à [quoi] grâce à [comment].",
                verticalOther: "Préciser la verticale",
                verticalOtherHint:
                    "Optionnel — uniquement si 'Autre' sélectionné ci-dessus.",
                needs: "Ce dont vous avez besoin",
                needsHint:
                    "Sélectionnez tout ce qui s'applique (au moins un).",
                challenges:
                    "Quel est le problème concret que vous ne pouvez résoudre seul ?",
                challengesPlaceholder:
                    "Soyez précis. Qu'est-ce qui vous bloque actuellement ?",
                revenue: "Revenu",
                targetGeo: "Géographie cible principale",
                targetGeoPlaceholder: "ex. DACH, France, Nordiques",
                selectPlaceholder: "Sélectionner…",
                otherOption: "Autre — préciser ci-dessous",
            },
            consentBefore:
                "J'accepte que mes données soient traitées par 0g Holding SA dans le cadre d'une qualification advisory, conformément à la",
            consentLink: "politique de confidentialité",
            consentAfter: ".",
            submit: "Soumettre votre projet",
            submitting: "Envoi…",
            submitNote:
                "Aucun deck requis. Pas de conseil gratuit. Soumissions qualifiées uniquement.",
            success:
                "Soumission reçue. Nous lisons chaque projet personnellement — si l'adéquation est là, vous aurez de nos nouvelles sous 48 heures.",
            errorGeneric:
                "Une erreur est survenue. Réessayez ou écrivez à info@alpe.vc.",
            errorMissing: "Champ manquant :",
            errorAtLeastOne:
                "Sélectionnez au moins un élément dans « Ce dont vous avez besoin ».",
            errorEmail: "Veuillez entrer une adresse email valide.",
            errorConsent:
                "Vous devez accepter la politique de confidentialité pour soumettre votre projet.",
            errorRateLimit:
                "Trop de soumissions depuis votre réseau. Réessayez dans une heure ou écrivez à info@alpe.vc.",
        },
        footer: {
            initiative: "Alpe.vc — une initiative de 0g Holding SA.",
            address: "Rue du Cavour 1, 1203 Genève, Suisse",
            colNavigation: "Navigation",
            colEcosystem: "Réseau & Écosystème",
            ecosystemA: "Actif dans 70+ pays.",
            ecosystemB:
                "Connecté à 60+ VCs, Family Offices & associations de Business Angels.",
            comingNote: "(US, Qatar, Singapour — bientôt)",
            rights: "© 2025 Alpe.vc — Tous droits réservés",
            legalLink: "Mentions légales",
            privacyLink: "Confidentialité",
            hostedOn: "Hébergé sur Vercel",
            dataController: "Responsable de traitement : 0g Holding SA (CH)",
        },
        legalShared: {
            backHome: "Retour à l'accueil",
            lastUpdated: "Dernière mise à jour · Mai 2026",
        },
        legal: {
            title: "Mentions légales",
            s1Label: "Éditeur",
            s1Heading: "Éditeur du site",
            s1Body: [
                "Alpe.vc est une initiative de 0g Holding SA, société anonyme suisse inscrite dans le canton de Genève.",
                [
                    "Raison sociale : 0g Holding SA",
                    "Siège social : Rue du Cavour 1, 1203 Genève, Suisse",
                    "Contact : info@alpe.vc",
                    "Directeur de la publication : Eric Brisset",
                ],
            ],
            s2Label: "Hébergement",
            s2Heading: "Hébergement du site",
            s2Body: [
                "Le site alpe.vc est hébergé par Vercel Inc., 440 N Barranca Avenue #4133, Covina, CA 91723, USA — vercel.com.",
                "L'API backend et la base de données (Supabase) sont hébergées au sein de l'Union européenne (régions Irlande / Francfort).",
            ],
            s3Label: "Propriété intellectuelle",
            s3Heading: "Propriété du contenu",
            s3Body: [
                "L'ensemble du contenu publié sur alpe.vc — y compris, sans limitation, les textes, le logo, l'identité graphique, les photographies, illustrations, structure et code source — est la propriété exclusive de 0g Holding SA, sauf mention contraire.",
                "Toute reproduction, représentation, modification, publication ou adaptation d'un élément du site, par quelque procédé que ce soit, est interdite sans l'autorisation écrite préalable de 0g Holding SA.",
            ],
            s4Label: "Responsabilité",
            s4Heading: "Limitation de responsabilité",
            s4Body: [
                "Les informations diffusées sur alpe.vc le sont à titre informatif uniquement et ne constituent en aucun cas un conseil en investissement, juridique, comptable, fiscal ou tout autre conseil professionnel. 0g Holding SA n'apporte aucune garantie quant à l'exhaustivité, l'exactitude ou l'actualité des informations présentées.",
                "0g Holding SA ne saurait être tenue responsable de tout dommage direct ou indirect résultant de l'accès au site alpe.vc ou de son utilisation.",
            ],
            s5Label: "Droit applicable",
            s5Heading: "Droit applicable & juridiction",
            s5Body: [
                "Le site alpe.vc et toute relation découlant de son utilisation sont régis par le droit suisse.",
                "Tout litige qui ne pourrait être résolu à l'amiable relève de la compétence exclusive des tribunaux du canton de Genève, Suisse.",
            ],
        },
        privacy: {
            title: "Politique de confidentialité",
            s1Label: "Vue d'ensemble",
            s1Heading: "Qui sommes-nous",
            s1Body: [
                "Cette politique de confidentialité décrit comment 0g Holding SA, agissant en tant que responsable de traitement au sens du Règlement européen 2016/679 (RGPD) et de la Loi fédérale suisse sur la protection des données (LPD), collecte, utilise et protège les données personnelles soumises via le site alpe.vc.",
                "Pour toute question ou demande relative à vos données personnelles, contactez-nous à info@alpe.vc.",
            ],
            s2Label: "Données collectées",
            s2Heading: "Ce que nous collectons",
            s2Body: [
                "Nous ne collectons que les données que vous nous fournissez activement via le formulaire de soumission. Plus précisément :",
                [
                    "Identification : prénom, nom, email professionnel, téléphone, nom de société",
                    "Information sur le projet : verticale, stade, description en une phrase, fourchette de revenu, géographie cible",
                    "Besoins et défis : domaines pour lesquels vous cherchez un accompagnement, description en texte libre de vos blocages actuels",
                ],
                "Aucun cookie analytique, aucun pixel de tracking, aucun traceur publicitaire tiers n'est déployé sur ce site.",
            ],
            s3Label: "Finalité & base légale",
            s3Heading: "Pourquoi nous traitons vos données",
            s3Body: [
                "Vos données sont traitées dans le seul but d'évaluer une potentielle relation advisory entre vous et Alpe.vc, ce qui inclut la qualification de votre soumission et notre prise de contact en cas d'adéquation.",
                "Bases légales (RGPD Art. 6) :",
                [
                    "Votre consentement explicite donné via le formulaire (Art. 6.1.a)",
                    "Notre intérêt légitime à évaluer des opportunités advisory et à gérer les échanges précontractuels (Art. 6.1.f)",
                ],
            ],
            s4Label: "Destinataires",
            s4Heading: "Qui a accès à vos données",
            s4Body: [
                "Vos données sont accessibles exclusivement à Eric Brisset et aux personnes autorisées de 0g Holding SA. Elles ne sont jamais vendues, louées ou partagées à des tiers à des fins marketing.",
                "Nous nous appuyons sur les sous-traitants suivants pour opérer le service :",
                [
                    "Supabase (hébergement de la base de données — régions UE, Irlande / Francfort)",
                    "Google Workspace / Gmail (livraison des emails — sous EU-US Data Privacy Framework)",
                    "Vercel (hébergement du site — sous EU-US Data Privacy Framework)",
                ],
            ],
            s5Label: "Conservation",
            s5Heading: "Durée de conservation",
            s5Body: [
                "Les soumissions qui ne donnent pas lieu à un engagement sont conservées au maximum 24 mois puis supprimées de notre base active.",
                "En cas d'engagement advisory, vos données sont conservées pour la durée de l'engagement et pour la durée légale de conservation des documents commerciaux applicable en droit suisse (généralement 10 ans).",
            ],
            s6Label: "Vos droits",
            s6Heading: "Vos droits selon RGPD / LPD",
            s6Body: [
                "Vous disposez du droit d'accès, de rectification, d'effacement, de limitation et d'opposition au traitement de vos données personnelles, ainsi que du droit à la portabilité.",
                "Pour exercer l'un de ces droits, écrivez à info@alpe.vc. Nous répondons sous 30 jours.",
                "Si vous estimez que vos droits ne sont pas respectés, vous pouvez également déposer une plainte auprès de l'autorité de contrôle compétente — le Préposé fédéral à la protection des données et à la transparence (PFPDT) en Suisse, ou votre autorité de protection des données locale dans l'UE.",
            ],
            s7Label: "Sécurité",
            s7Heading: "Comment nous protégeons vos données",
            s7Body: [
                "Les données sont transmises en HTTPS et stockées dans une base chiffrée. L'accès est restreint au personnel autorisé sous authentification stricte.",
                "Malgré toutes les précautions raisonnables, aucune méthode de transmission ou de stockage n'est 100% sécurisée. Nous nous engageons à vous notifier ainsi que l'autorité compétente sans délai indu en cas de violation affectant vos données personnelles.",
            ],
        },
        verticalOptions: {
            IoT: "IoT",
            Health: "Santé",
            AI: "IA",
            Climate: "Climat",
            "Industrial & Infrastructure": "Industrie & Infrastructure",
            "Deep Tech": "Deep Tech",
            Other: "Autre",
        },
        stageOptions: {
            "First customers": "Premiers clients",
            "Established traction": "Traction établie",
            Scale: "Scale",
        },
        revenueOptions: {
            "Pre-revenue": "Pré-revenu",
            "<50k€": "<50k€",
            "50k–200k€": "50k–200k€",
            "200k–1M€": "200k–1M€",
            "1M€+": "1M€+",
        },
        needsOptions: {
            Capital: "Capital",
            "GTM / Market access": "GTM / Accès marché",
            "Europe entry": "Entrée en Europe",
            "Board / Governance": "Board / Gouvernance",
            Combination: "Combinaison",
        },
    },
};
